import type { ReactNode } from 'react';

import { DataType, type SecondaryDimensionValues } from '@revelio/core';

import type { LocationType } from '../Chart/Map/types';
import type { MetricValues } from '../PageConfigProvider/types/metric';
export { DataType };

export type SeriesType = 'primary' | 'benchmark' | 'other';

export type SingleDataPoint<T extends object = object> = T & {
  dim: string;
  longName?: string | null;
  value: number | null;
  share?: number | null;
  id?: string | null;
  drilldownId?: string | null;
  seriesType?: SeriesType;
  color?: string;
  /**
   * Segment size (headcount) when the plotted metric is a RATE, so a consumer
   * can say how big the segment is as well as how fast it moved. Never
   * rendered by charts — the assistant's breakdown digest reads it so answers
   * can lead with sizes ("Sales, 890 people, +14%").
   */
  segmentHeadcount?: number | null;
};
export type SingleDataSet<T extends object = object> = {
  type: DataType.SINGLE;
  data: SingleDataPoint<T>[];
};

export type TimeseriesDataPoint = {
  date: string;
  value: number | null;
  share?: number | null;
  secondaryValue?: number | null;
};

export type TimeseriesData = {
  id?: string | null;
  dim: string;
  timeseries: TimeseriesDataPoint[];
};

export type TimeseriesDataSet = {
  type: DataType.TIMESERIES;
  data: TimeseriesData[];
};

export type BoxDataPoint = {
  q10: number;
  q25?: number; // Q1 quartile – populated when histogram data is available
  q50: number;
  q75?: number; // Q3 quartile – populated when histogram data is available
  q90: number;
};

export type PercentileDataPoint = {
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
};

export type BoxDataSet = {
  dim: string;
  id?: string | null;
  box: BoxDataPoint;
};

/**
 * Box plot dataset (non-breakdown).
 * Note: this dataset type is currently not produced by any CustomDash DataProvider,
 * but it is part of the shared DataType enum and may appear in future providers.
 */
export type BoxSeriesDataSet = {
  type: DataType.BOX;
  data: BoxDataSet[];
};

export type BreakdownData<
  T extends SingleDataPoint | TimeseriesDataPoint | BoxDataSet,
> = {
  dim: string;
  id?: string | null;
  breakdown: T[];
};

export type TransitionBreakdownData = BreakdownData<SingleDataPoint> & {
  inflowBreakdown?: SingleDataPoint[];
  outflowBreakdown?: SingleDataPoint[];
};

export type BreakdownSingleDataSet<
  T extends SingleDataPoint = SingleDataPoint,
> = {
  type: DataType.BREAKDOWN_SINGLE;
  data: BreakdownData<T>[];
};

export type NestedDataPoint = SingleDataPoint<{ children: SingleDataPoint[] }>;

export type NestedBreakdownSingleDataSet =
  BreakdownSingleDataSet<NestedDataPoint> & {
    levels: readonly [TableDimension, TableDimension];
  };

export type LeveledBreakdownSingleDataSet = BreakdownSingleDataSet & {
  level: TableDimension;
};

type FlowBreakdownSingleDataSet = {
  type: DataType.FLOW_BREAKDOWN_SINGLE;
  data: TransitionBreakdownData[];
};

export type BreakdownTimeseriesDataSet = {
  type: DataType.BREAKDOWN_TIMESERIES;
  data: BreakdownData<TimeseriesDataPoint>[];
};

/** Box plot with a secondary breakdown; uses {@link DataType.BOX} (discriminate with {@link isBoxBreakdownDataSet}). */
export type BreakdownBoxDataSet = {
  type: DataType.BOX;
  data: BreakdownData<BoxDataSet>[];
};

export type DistributionDataPoint = {
  value: number;
  kde: number;
  cdf: number;
};

export type DistributionData = {
  dim: string;
  id?: string | null;
  distribution: DistributionDataPoint[];
  percentiles?: Pick<PercentileDataPoint, 'p25' | 'p50' | 'p75'>;
};

export type DistributionDataSet = {
  type: DataType.DISTRIBUTION;
  data: DistributionData[];
};

export type MapDataSet = {
  type: DataType.MAP;
  points: Array<{
    id: string;
    label: string;
    lat: number;
    lon: number;
    value: number;
    tooltip?: Record<string, number | string>;
    locationType: LocationType;
  }>;
};

export type TableRowsDataSet = {
  type: DataType.TABLE_ROWS;
  data: Array<Record<string, unknown>>;
};

export type TextDataSet = {
  type: DataType.TEXT;
  data: Array<{
    entity: string;
    id?: string | null;
    text: string;
    metadata?: Record<string, unknown>;
  }>;
};

export type DataProviderSet =
  | SingleDataSet
  | TimeseriesDataSet
  | BreakdownSingleDataSet
  | FlowBreakdownSingleDataSet
  | BreakdownTimeseriesDataSet
  | BreakdownBoxDataSet
  | BoxSeriesDataSet
  | DistributionDataSet
  | MapDataSet
  | TableRowsDataSet
  | TextDataSet;

export type LevelDimension =
  | `role_lvl_${1 | 2 | 3 | 4 | 5 | 6 | 7}`
  | `skill_lvl_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8}`
  | `activity_lvl_${1 | 2 | 3 | 4 | 5 | 6}`
  | `rics_k${10 | 50 | 400}`
  | 'region'
  | 'country'
  | 'state'
  | 'msa'
  | 'county'
  | 'city';

export type TableDimension = SecondaryDimensionValues | LevelDimension;
export type TableDimensionValue = { id: string; label: string };

export type TableDimensionColumn = {
  values: TableDimensionValue[];
  indices: Uint32Array;
};

/**
 * {
 *   rowCount: 2,
 *   dimensions: ['company'],
 *   metrics: ['headcount'],
 *   dimensionColumns: {
 *     company: {
 *       values: [{ id: '1116644', label: 'Workday' }, { id: '1', label: 'Acme' }],
 *       indices: Uint32Array [0, 1],
 *     },
 *   },
 *   metricColumns: { headcount: Float64Array [10000, NaN] },
 * }
 */
export type TableData = {
  rowCount: number;
  dimensions: TableDimension[];
  metrics: MetricValues[];
  dimensionColumns: Partial<Record<TableDimension, TableDimensionColumn>>;
  metricColumns: Partial<Record<MetricValues, Float64Array>>;
};

export enum DataProviderQueryStatus {
  Idle = 'idle',
  Fetching = 'fetching',
  Error = 'error',
  Success = 'success',
}

export interface DataProviderResult<T = unknown> {
  data: T | undefined;
  status: DataProviderQueryStatus;
  error?: Error;
  haveSelectionsBeenModified?: boolean;
  /** Month labels derived from request filters when the API omits `refs.months` (Composition V2.1). */
  monthRefs?: string[];
}

export type DataProviderChildren<T = unknown> = (
  props: DataProviderResult<T>
) => ReactNode;

export const isSingleDataSet = (data: DataProviderSet): data is SingleDataSet =>
  data.type === DataType.SINGLE;

export const isNestedBreakdownDataSet = (
  data: DataProviderSet
): data is NestedBreakdownSingleDataSet =>
  data.type === DataType.BREAKDOWN_SINGLE && 'levels' in data;

export const isLeveledBreakdownDataSet = (
  data: DataProviderSet
): data is LeveledBreakdownSingleDataSet =>
  data.type === DataType.BREAKDOWN_SINGLE && 'level' in data;

export const isBoxBreakdownDataSet = (
  data: DataProviderSet
): data is BreakdownBoxDataSet => {
  if (data.type !== DataType.BOX) return false;
  const first = data.data[0];
  return first !== undefined && 'breakdown' in first;
};

export const isBoxDataPoint = (value: unknown): value is BoxDataPoint => {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as BoxDataPoint).q10 === 'number' &&
    typeof (value as BoxDataPoint).q50 === 'number' &&
    typeof (value as BoxDataPoint).q90 === 'number'
  );
};
